import { utilities } from "@ObamaFoundation/of-design-system";

// Chicago timezone constant for consistent date/time handling
export const CHICAGO_TZ = "America/Chicago";

export const isDatePickerFormat = (date) =>
  typeof date === "string" && date.match(/^\d{4}-\d{2}-\d{2}$/);

export const toChicagoDate = (date) => {
  // If we're passed plain date object
  if (date instanceof Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  // If we're passed a YYYY-MM-DD string from the date picker
  if (isDatePickerFormat(date)) {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // For event dates with timezone info
  const d = new Date(date);
  const chicagoDateStr = d.toLocaleString("en-US", {
    timeZone: CHICAGO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour12: false
  });
  const [month, day, year] = chicagoDateStr.split(",")[0].split("/");
  return new Date(year, month - 1, day);
};

export const isAfterDay = (date1, date2) => {
  const d1 = toChicagoDate(date1);
  const d2 = toChicagoDate(date2);
  return (
    d1.getFullYear() > d2.getFullYear() ||
    (d1.getFullYear() === d2.getFullYear() &&
      (d1.getMonth() > d2.getMonth() ||
        (d1.getMonth() === d2.getMonth() && d1.getDate() > d2.getDate())))
  );
};

export const formatDateForCalendar = (date) => {
  if (!date) {
    return "";
  }

  const d = date instanceof Date ? date : toChicagoDate(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getTodayDate = () => {
  const now = new Date();
  return formatDateForCalendar(now);
};

// Helper function to get the Monday of the week for a given date
export const getWeekStart = (date) => {
  const d = new Date(toChicagoDate(date));
  const day = d.getDay() || 7; // Convert Sunday (0) to 7 for easier calculation
  const diff = d.getDate() - day + 1; // Adjust to previous Monday
  d.setDate(diff);
  return d;
};

// Helper function to get the end of the week (Sunday) given any date in the week
export const getWeekEnd = (weekStartOrDate) => {
  const start =
    weekStartOrDate.getDay && weekStartOrDate.getDay() === 1
      ? new Date(weekStartOrDate)
      : getWeekStart(weekStartOrDate);
  const end = new Date(start);
  end.setDate(start.getDate() + 6); // Add 6 days to get to Sunday
  return end;
};

export const formatDateRange = (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : toChicagoDate(startDate);
  const end = endDate
    ? endDate instanceof Date
      ? endDate
      : toChicagoDate(endDate)
    : getWeekEnd(start);

  const startMonth = start.toLocaleString("en-US", { month: "long" });
  const endMonth = end.toLocaleString("en-US", { month: "long" });

  const startDay = start.getDate();
  const endDay = end.getDate();

  if (start.getTime() === end.getTime()) {
    return `${startMonth} ${startDay}`;
  }

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}`;
  }

  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
};

export const getBoundaryDate = (items = [], findLatest = false) => {
  const validDates = items.filter((item) => item?.start).map((item) => new Date(item.start));

  if (!validDates.length) {
    return "";
  }

  const boundaryDate = findLatest
    ? new Date(Math.max(...validDates))
    : new Date(Math.min(...validDates));

  return formatDateForCalendar(boundaryDate);
};

export function formatTime(dateTime) {
  try {
    const time = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/Chicago"
    }).format(new Date(dateTime));

    return utilities.toApStyleTime(time);
  } catch {
    return "Invalid date";
  }
}

export function formatDuration(startDateTime, endDateTime) {
  try {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    const diffInMs = end - start;
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;

    let durationString = "";
    if (hours > 0) {
      durationString += `${hours} hr${hours > 1 ? "s" : ""}`;
    }
    if (minutes > 0) {
      if (hours > 0) {
        durationString += " ";
      }
      durationString += `${minutes} min${minutes > 1 ? "s" : ""}`;
    }

    return durationString;
  } catch (e) {
    return "Invalid duration";
  }
}

export const generateMonthOptions = (startDate, events = []) => {
  const date = toChicagoDate(startDate);

  // Find the last month that has events
  let lastEventMonth = null;
  if (events.length > 0) {
    const validEvents = events.filter((event) => event?.start);
    if (validEvents.length > 0) {
      const latestEvent = validEvents.reduce((latest, event) => {
        const eventDate = new Date(event.start);
        return eventDate > latest ? eventDate : latest;
      }, new Date(validEvents[0].start));
      lastEventMonth = new Date(latestEvent.getFullYear(), latestEvent.getMonth(), 1);
    }
  }

  // Calculate how many months to show
  let monthsToShow = 12; // Default max
  if (lastEventMonth) {
    const startMonthDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const monthsDiff =
      (lastEventMonth.getFullYear() - startMonthDate.getFullYear()) * 12 +
      (lastEventMonth.getMonth() - startMonthDate.getMonth()) +
      1;
    monthsToShow = Math.min(monthsDiff, 12);
  }

  const monthsData = [];
  const yearGroups = {};

  for (let i = 0; i < monthsToShow; i++) {
    const monthDate = new Date(date.getFullYear(), date.getMonth() + i, 1);
    const yearValue = monthDate.getFullYear();
    const monthName = monthDate.toLocaleString("en-US", { month: "long" });
    const value = `${yearValue}-${String(monthDate.getMonth() + 1).padStart(2, "0")}-01`;

    if (!yearGroups[yearValue]) {
      yearGroups[yearValue] = [];
    }

    yearGroups[yearValue].push({
      label: monthName,
      value
    });
  }

  for (const [yearKey, months] of Object.entries(yearGroups)) {
    monthsData.push({
      label: yearKey,
      months
    });
  }

  return monthsData;
};

/**
 *
 * @param {*} currentDate
 * @param {Number} month the selected month; if left empty, this defaults to the current date's month.
 * @param {Number} year the selected year; if left empty, this defaults to the current date's year.
 * @returns
 */
export const getInitialSelectedMonth = (currentDate, month, year) => {
  const date = toChicagoDate(currentDate);
  const yearValue = year || date.getFullYear();
  const monthValue = String(month || date.getMonth() + 1).padStart(2, "0");
  return `${yearValue}-${monthValue}-01`;
};

export const getNextMonth = (currentMonth) => {
  const date = toChicagoDate(currentMonth);
  const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  return `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-01`;
};
